import React, { useRef, useEffect } from 'react'
import { string, shape, func, bool } from 'prop-types'
import { createComponent } from '@ycos/fela'
import { getFontStyles } from '@ycos/primitives'
import { SingleColumn } from '@ycos/component-columns'
import Markdown from './Markdown'

const InfoBox = createComponent('InfoBox', () => ({
  direction: 'ltr',
  display: 'block',
  width: '100%',
  height: '100%',
  position: 'relative'
}))

const InfoWrapper = createComponent('InfoWrapper', ({ allowCampaignBanner }) => ({
  position: 'absolute',
  bottom: allowCampaignBanner ? '32px' : '48px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& a': {
    textDecoration: 'none'
  },
  'screen-medium': {
    bottom: '56px',
    width: '550px'
  }
}))

const CampaignButtons = createComponent('CampaignButtons', ({ theme, loginPromo, allowCampaignBanner, allowMultipleCta }) => ({
  display: allowCampaignBanner ? 'none' : 'flex',
  flexDirection: loginPromo ? 'row' : 'column',
  marginTop: `${theme.spacingMultiplier * 3}px`,
  gap: '16px',
  'screen-medium': {
    display: 'flex',
    flexDirection: 'row',
    marginTop: allowMultipleCta ? `${theme.spacingMultiplier * 3}px` : `${theme.spacingMultiplier * 2}px`
  }
}))

const CampaignHeading = createComponent('CampaignHeading', ({ theme, allowSkinny, allowSkinnySale, language, loginPromo, brand }) => {
  const allSkinnyStyle =
    !loginPromo && allowSkinny && brand !== 'theoutnet'
      ? {
        fontStyle: 'italic'
      }
      : {}

  // JP, KO and AR have them own font
  const isLanguageHaveOwnFont = ['jp', 'ko', 'ar'].includes(language.toLowerCase())

  const allSkinnyStyleSale =
    allowSkinnySale && !isLanguageHaveOwnFont && brand !== 'theoutnet'
      ? {
        textTransform: 'uppercase !important',
        fontFamily: 'SackersGothicStd !important'
      }
      : {}

  return {
    ...getFontStyles(theme.typography, { type: 'Heading', tagName: 'div', name: theme?.custom?.campaignTitle.name }),
    lineHeight: theme?.custom?.campaignTitle.lineHeight,
    color: theme.typography.colors.PrimaryWhite,
    ...allSkinnyStyle,
    ...allSkinnyStyleSale,
    'screen-medium': {
      fontSize: '32px',
      ...allSkinnyStyleSale,
      lineHeight: theme?.custom?.campaignTitle.lineHeightDesktop
    }
  }
})

const CampaignInfo = createComponent('CampaignInfo', ({ theme }) => ({
  paddingTop: `${theme.spacingMultiplier}px`
}))

const CampaignButton = createComponent(
  'CampaignButton',
  ({ theme, allowMultipleCta, brand }) => {
    return {
      ...getFontStyles(theme.typography, { type: 'Label', tagName: 'p', name: '1' }),
      fontWeight: '400',
      boxSizing: 'border-box',
      justifyContent: 'center',
      opacity: '1',
      width: '100%',
      color: theme?.typography?.colors?.PrimaryBlack,
      backgroundColor: theme?.typography?.colors?.PrimaryWhite,
      lineHeight: brand === 'theoutnet' ? '16px' : '18px',
      letterSpacing: brand === 'theoutnet' ? '1px' : '0.14px',
      padding: '15px 16px',
      textAlign: 'center',
      border: `1px solid ${theme?.typography?.colors?.PrimaryWhite}`,
      borderRadius: '2px',
      ':hover': {
        opacity: '0.8',
        transition: 'opacity 300ms cubic-bezier(0.3, 0.0, 0.3, 1.0)'
      },
      ':nth-child(2)': {
        color: allowMultipleCta ? theme?.typography?.colors?.PrimaryBlack : theme?.typography?.colors?.PrimaryWhite,
        backgroundColor: allowMultipleCta ? theme?.typography?.colors?.PrimaryWhite : 'transparent',
        opacity: allowMultipleCta ? '1' : '0.6',
        ':hover': {
          opacity: allowMultipleCta ? '0.8' : '1',
          transition: 'opacity 300ms cubic-bezier(0.3, 0.0, 0.3, 1.0)'
        }
      },
      'screen-medium': {
        width: '240px',
        padding: '11px 16px'
      }
    }
  },
  'a'
)

function InfoBlock(props) {
  const {
    teaserTitle,
    teaserText,
    target,
    theme,
    onTargetClick,
    rootPosition,
    setInfoHeight,
    allowSkinny,
    allowSkinnySale,
    allowCampaignBanner,
    locale,
    brand,
    targets,
    loginPromo,
    optimizely
  } = props
  const { language } = locale
  const infoWrapperRef = useRef(null)
  const optimizelyTrack = (optimizelyEvent) => () => {
    optimizely?.track?.(optimizelyEvent)
  }
  useEffect(() => {
    if (infoWrapperRef.current) {
      const updateHeight = () => {
        setInfoHeight(infoWrapperRef.current.offsetHeight)
      }
      updateHeight()
      window.addEventListener('resize', updateHeight)
      return () => {
        window.removeEventListener('resize', updateHeight)
      }
    }
  }, [infoWrapperRef])

  // Need to pass this info to SingleColumn to avoid grid systems clashing
  const isTONRtl = language === 'ar' && brand === 'theoutnet'

  return (
    <SingleColumn item={{ layoutVariant: 'isc-single-column-large' }} noPaddingTop noPaddingTopMobile isTONRtl={isTONRtl}>
      <InfoBox data-testid="InfoBox">
        <InfoWrapper innerRef={infoWrapperRef} allowCampaignBanner={allowCampaignBanner} data-testid="InfoWrapper">
          <a href={target.href} onClick={() => onTargetClick()}>
            <CampaignHeading
              theme={theme}
              loginPromo={loginPromo}
              allowSkinny={allowSkinny}
              allowSkinnySale={allowSkinnySale}
              language={language}
              brand={brand}
            >
              {teaserTitle}
            </CampaignHeading>
          </a>
          {teaserText && (
            <CampaignInfo>
              <Markdown teaserText={teaserText} allowSkinnySale={allowSkinnySale} brand={brand} />
            </CampaignInfo>
          )}
          <CampaignButtons loginPromo={loginPromo} allowCampaignBanner={allowCampaignBanner} allowMultipleCta={targets?.length > 1}>
            {loginPromo
              ? targets.map(({ target }) => (
                <CampaignButton
                  href={target.href}
                  key={target?.contentID}
                  onClick={optimizelyTrack(target.analyticsTextString)}
                  brand={brand}
                  allowCampaignBanner={allowCampaignBanner}
                >
                  {target.teaserTitle}
                </CampaignButton>
              ))
              : targets.map(({ target }) => (
                <CampaignButton
                  key={target?.contentID}
                  href={target?.href}
                  onClick={() => onTargetClick({ specificItem: { href: target?.href, rootPosition } })}
                  brand={brand}
                  allowMultipleCta={targets?.length > 1}
                >
                  {target.teaserTitle || target?.teaser?.teaserTitle}
                </CampaignButton>
              ))}
          </CampaignButtons>
        </InfoWrapper>
      </InfoBox>
    </SingleColumn>
  )
}

InfoBlock.propTypes = {
  teaserTitle: string.isRequired,
  teaserText: string.isRequired,
  target: shape({
    teaserTitle: string.isRequired
  }).isRequired,
  locale: shape({
    language: string.isRequired
  }).isRequired,
  onTargetClick: func,
  brand: string,
  allowSkinny: bool,
  allowSkinnySale: bool
}

export default InfoBlock
